import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import "./tooltipProvider.scss";
import { TooltipWrapper } from "./TooltipWrapper";

// Create the context
const TooltipContext = createContext(undefined);

export const useTooltips = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error("Context must be used within a TooltipProvider");
  }
  return context;
};

// Create the provider component
export const TooltipProvider = ({ children }) => {
  const [tooltipState, setTooltipState] = useState({
    position: { clientX: 0, clientY: 0, vertical: 'top', horizontal: 'center' },
    targetId: null,
  });

  const dialogRef = useRef(null);

  const showTooltip = useCallback((targetId, x, y) => {
    setTooltipState({
      position: { clientX: x, clientY: y, vertical: 'top', horizontal: 'center' },
      targetId: targetId,
    });
  }, []);

  const hideTooltip = () => {
    setTooltipState(prevState => {
      // Only create a new state object if targetId was not already null
      if (prevState.targetId !== null) {
        return {
          position: { clientX: 0, clientY: 0, vertical: 'top', horizontal: 'center' },
          targetId: null,
        };
      }
      return prevState;
    });

    dialogRef.current.close();
  }

  // Close tooltip on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipState.targetId && dialogRef.current && !dialogRef.current.contains(event.target)) {
        hideTooltip();
      }
    };

    // Add event listener when the tooltip is shown
    if (tooltipState.targetId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Remove event listener when the tooltip is hidden
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltipState.targetId, hideTooltip]);

  return (
    <TooltipContext.Provider
      value={{
        showTooltip,
        hideTooltip,
        tooltipState
      }}
    >
      {children}
      <TooltipWrapper
        dialogRef={dialogRef}
        tooltipState={tooltipState}
      />
    </TooltipContext.Provider>
  );
};
